import { useCallback, useLayoutEffect, useState } from 'react';
import { GoogleMap, Marker, Polygon, Autocomplete } from '@react-google-maps/api';
import {
  Button,
  DialogContent,
  DialogFooter,
  Input,
  Notice,
  withDialog,
  type WithDialogProps,
} from '@kolking/react-ui';

import { POLYGON_PATHS } from '../../models';

import styles from './styles.module.scss';

const MAP_OPTIONS = {
  fullscreenControl: false,
  streetViewControl: false,
  mapTypeControlOptions: {
    style: 2.0,
  },
};

const MAP_CENTER = {
  lat: -18.193775,
  lng: 133.217285,
};

const MAP_POLYGON_OPTIONS = {
  fillColor: 'green',
  fillOpacity: 0.1,
  strokeColor: 'green',
  strokeOpacity: 1,
  strokeWeight: 1,
};

const AUTOCOMPLETE_OPTIONS = {
  strictBounds: true,
};

type Props = {
  value?: google.maps.LatLngLiteral;
};

export type LocationDialogProps = WithDialogProps<Props, google.maps.LatLngLiteral>;

const Dialog = ({ dialog, value }: LocationDialogProps) => {
  const [australiaZone] = useState(() => new google.maps.Polygon({ paths: POLYGON_PATHS }));
  const [map, setMap] = useState<google.maps.Map>();
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete>();
  const [position, setPosition] = useState<google.maps.LatLngLiteral>();
  const [invalid, setInvalid] = useState(true);

  useLayoutEffect(() => {
    if (value) {
      setPosition(value);
    } else {
      navigator.geolocation.getCurrentPosition(
        (location) => {
          setPosition({
            lat: location.coords.latitude,
            lng: location.coords.longitude,
          });
        },
        () => setPosition(MAP_CENTER),
      );
    }
  }, [value]);

  useLayoutEffect(() => {
    setInvalid(
      position == null || !google.maps.geometry.poly.containsLocation(position, australiaZone),
    );
  }, [australiaZone, position]);

  useLayoutEffect(() => {
    if (autocomplete) {
      const bounds = new google.maps.LatLngBounds();

      POLYGON_PATHS.forEach((location) => {
        bounds.extend(location);
      });

      autocomplete.setBounds(bounds);
    }
  }, [autocomplete]);

  const handleLoad = useCallback((map: google.maps.Map) => {
    map.setCenter(MAP_CENTER);
    map.setZoom(4);

    setMap(map);
  }, []);

  const handleUnmount = useCallback(() => {
    setMap(undefined);
  }, []);

  // const handleBoundsChanged = useCallback(() => {
  //   if (autocomplete) {
  //     autocomplete.setBounds(map?.getBounds());
  //   }
  // }, [map, autocomplete]);

  const handlePlacesChanged = useCallback(() => {
    const location = autocomplete?.getPlace().geometry?.location;

    if (location) {
      setPosition(location.toJSON());

      if (map) {
        map.setZoom(13);
        map.setCenter(location);
      }
    }
  }, [map, autocomplete]);

  const handlePolygonClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      setPosition(e.latLng.toJSON());
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (position) {
      dialog.confirm(position);
    } else {
      dialog.cancel();
    }
  }, [dialog, position]);

  return (
    <>
      <DialogContent>
        <GoogleMap
          mapContainerClassName={styles.map}
          options={MAP_OPTIONS}
          onLoad={handleLoad}
          onUnmount={handleUnmount}
          // onBoundsChanged={handleBoundsChanged}
        >
          <Autocomplete
            options={AUTOCOMPLETE_OPTIONS}
            onLoad={setAutocomplete}
            onPlaceChanged={handlePlacesChanged}
          >
            <Input type="search" className={styles.searchbox} placeholder="Search..." />
          </Autocomplete>
          <Polygon
            paths={POLYGON_PATHS}
            options={MAP_POLYGON_OPTIONS}
            onClick={handlePolygonClick}
          />
          {position && <Marker position={position} animation={google.maps.Animation.DROP} />}
        </GoogleMap>
      </DialogContent>
      <DialogFooter className={styles.footer}>
        {invalid && (
          <Notice size="sm" scheme="warning" variant="plain" margin={0}>
            The selected location is outside of the Northern Australia area.
          </Notice>
        )}
        <Button type="button" variant="secondary" title="Close map" onClick={dialog.cancel} />
        <Button type="button" disabled={invalid} title="Save location" onClick={handleSubmit} />
      </DialogFooter>
    </>
  );
};

export const LocationDialog = withDialog<Props, google.maps.LatLngLiteral>(Dialog, { size: 'xl' });
